import { PARAGRAPH_OR_SOFT_BREAK, SOFT_LINE_BREAK, TEXT_LAYOUT_PARAGRAPHS, textLayoutAtLeast } from './constants';
import { splitSegmentAtHyphen, splitSegmentWith, type HyphenationRules, type WordLanguages } from './hyphenate';

/**
 * Функция измерения ширины текста. Инжектируется вызывающей стороной,
 * чтобы либа не зависела от opentype.js/Sharp/Canvas.
 *
 * Должна возвращать ширину в тех же единицах, что и `maxWidth`
 * (обычно — книжные пиксели после масштабирования).
 */
export type MeasureTextWidth = (text: string) => number;

/** {@link MeasureTextWidth} that also gets the source code-unit offset the candidate starts at (multi-run text). */
export type MeasureTextAt = (text: string, start: number) => number;

/**
 * Строка после переноса вместе с признаком конца абзаца.
 *
 * `paragraphEnd` = строка завершает исходный абзац (последняя перед `\n` или в конце текста).
 * Нужен выключке (`align: 'justify'`): Photoshop растягивает межсловные интервалы на всех строках
 * абзаца, КРОМЕ последней — её оставляет по стороне выравнивания. Восстановить признак снаружи
 * нельзя: перенос съедает ведущие пробелы продолжения, и границы абзаца в плоском списке теряются.
 */
export interface WrappedLine {
  readonly text: string;
  readonly paragraphEnd: boolean;
  /** Строка оборвана переносом по слогам: её последний символ — дефис, которого в исходном тексте нет. */
  readonly hyphenated?: boolean;
  /**
   * `textLayout` 7: the line ends at a soft return (U+2028) — a forced break inside its paragraph; justification leaves
   * it unstretched. Set only on such lines (a paragraph end is forced by `paragraphEnd`) — mirror of Rust `line_forced`.
   */
  readonly lineForced?: boolean;
}

/** The line ends at a forced break: a paragraph end or a soft return. */
export function isForcedLineEnd(line: WrappedLine): boolean {
  return line.paragraphEnd || line.lineForced === true;
}

/**
 * Жадная сборка строк с переносом по слогам: слово, не влезшее целиком, по возможности делится
 * дефисом, и только потом уходит на следующую строку целиком; слово шире строки режется по слогам,
 * а не по буквам. `maxConsecutive` держит предел подряд идущих строк с дефисом.
 */
export class LineBuilder {
  readonly lines: WrappedLine[] = [];
  /** Source offset where the segment being placed ends: the current line and every split fragment end there. */
  segEnd = 0;
  /** Told how each line starts (see {@link ParagraphWidths.lineStart}). */
  lineStart: ((word: number, start: number) => void) | undefined;
  private consecutive = 0;

  /**
   * `afterBreak` — вызывается после каждой строки, закрытой внутри абзаца: area-перенос проматывает в нём
   * полосы вне контура, чтобы следующая строка не легла на мёртвую полосу.
   */
  constructor(
    private readonly measureWidth: MeasureTextAt,
    private readonly hyphenation: HyphenationRules | undefined,
    private readonly afterBreak: () => void = () => undefined,
    private readonly languages?: WordLanguages,
  ) {}

  push(text: string, paragraphEnd: boolean, hyphenated = false): void {
    this.lines.push(hyphenated ? { text, paragraphEnd, hyphenated } : { text, paragraphEnd });
    this.consecutive = hyphenated ? this.consecutive + 1 : 0;
  }

  /** A line closed by a soft return (`textLayout` 7): forced, but its paragraph goes on. */
  pushSoft(text: string): void {
    this.lines.push({ text, paragraphEnd: false, lineForced: true });
    this.consecutive = 0;
  }

  /**
   * A new line starts with `text` (it ends at `segEnd`): tells {@link lineStart} its first word's break width and the
   * source offset it starts at.
   */
  startsLine(text: string): void {
    const lineStart = this.lineStart;
    if (!lineStart) return;
    const start = Math.max(0, this.segEnd - text.length);
    const word = breakSegments(text)[0];
    lineStart(word === undefined ? 0 : measureBreakWidthAt(word, start, this.measureWidth), start);
  }

  /** Break width of `text` ending at the segment being placed (`segEnd`). */
  widthEndingHere(text: string): number {
    return measureBreakWidthAt(text, Math.max(0, this.segEnd - text.length), this.measureWidth);
  }

  /** Кусок `segment`, влезающий в `maxWidth` вместе с `prefix` и дефисом; `undefined` — переносить нельзя. */
  split(prefix: string, segment: string, maxWidth: number) {
    const rules = this.hyphenation;
    if (!rules || (rules.maxConsecutive > 0 && this.consecutive >= rules.maxConsecutive)) return undefined;
    // `prefix` ends where `segment` starts, and `segment` ends at `segEnd`: every head starts at the prefix.
    const headStart = Math.max(0, this.segEnd - segment.length - prefix.length);
    const fits = (head: string): boolean => measureBreakWidthAt(head, headStart, this.measureWidth) <= maxWidth;
    const languages = this.languages;
    if (!languages) return splitSegmentAtHyphen(prefix, segment, rules, fits);
    const start = this.segEnd - segment.length;
    return splitSegmentWith(prefix, segment, (word, at) => languages.points(word, start + at, rules), fits);
  }

  /** Сегмент не влез в строку с уже набранным текстом: либо хвост слова остаётся, либо строка закрывается целиком. */
  breakBefore(currentLine: string, segment: string, maxWidth: number): string {
    const split = this.split(currentLine, segment, maxWidth);
    if (split) {
      this.push(split.head, false, true);
      this.afterBreak();
      this.startsLine(split.rest);
      return split.rest;
    }

    this.push(currentLine, false);
    this.afterBreak();
    const rest = segment.trimStart();
    this.startsLine(rest);
    return rest;
  }

  /** Строка шире контейнера сама по себе: сперва по слогам, затем побуквенно (break-word). */
  breakOverflow(currentLine: string, maxWidth: () => number): string {
    let line = currentLine;
    while (this.widthEndingHere(line) > maxWidth()) {
      const split = this.split('', line, maxWidth());
      if (!split) break;
      this.push(split.head, false, true);
      this.afterBreak();
      this.startsLine(split.rest);
      line = split.rest;
    }

    if (this.widthEndingHere(line) <= maxWidth()) return line;
    const chars = [...line];
    let lineStart = Math.max(0, this.segEnd - line.length);
    line = '';
    for (const [k, ch] of chars.entries()) {
      const test = line + ch;
      if (measureBreakWidthAt(test, lineStart, this.measureWidth) > maxWidth() && line) {
        lineStart += line.length;
        this.push(line, false);
        this.afterBreak();
        if (this.lineStart) this.startsLine(chars.slice(k).join(''));
        line = ch;
      } else {
        line = test;
      }
    }

    return line;
  }
}

/**
 * Невидимый хвост строки: Unicode White_Space плюс ZWNBSP (`U+FEFF`). JS `\s` не знает NEL (`U+0085`),
 * а Rust-зеркало (`char::is_whitespace`) не знает ZWNBSP — берём объединение, иначе два рантайма резали
 * бы разное и печать разошлась бы с экраном на символах, которых нет в golden-фикстурах.
 */
const TRAILING_INVISIBLE = /[\s\u0085]+$/u;

/**
 * Видимая часть строки — до последнего непробельного глифа.
 *
 * Хвостовые пробелы висят ЗА краем строки: Photoshop не считает их ни в бюджете переноса, ни в
 * выравнивании — правый край строки ставит последний ВИДИМЫЙ глиф. Пробелы при этом остаются в
 * тексте (и в отрисовке), просто свисают наружу. SSoT правила: и перенос, и выключка, и выравнивание
 * режут хвост одним и тем же предикатом.
 */
export function visibleLineText(text: string): string {
  return text.replace(TRAILING_INVISIBLE, '');
}

/**
 * Ширина строки-кандидата для решения о переносе.
 *
 * Считается по {@link visibleLineText}: меряя хвостовые пробелы, мы отнимали у строки ширину
 * целого пробела — при трекинге это ещё и трекинг пробела, и очередное слово срывалось на
 * следующую строку, а выключка растягивала оставшиеся интервалы в ямы.
 */
export function measureBreakWidth(text: string, measureWidth: MeasureTextWidth): number {
  return measureWidth(visibleLineText(text));
}

/** {@link measureBreakWidth} of a candidate starting at source offset `start`. */
function measureBreakWidthAt(text: string, start: number, measureWidth: MeasureTextAt): number {
  return measureWidth(visibleLineText(text), start);
}

/** UAX#14-lite break opportunities of one line (mirror of Rust `break_segments`). */
export function breakSegments(line: string): string[] {
  return line.split(/(?<=[\s\-/\u2010\u2012\u2013\u2014])|(?=\u2014)/);
}

/**
 * Автоперенос строк по ширине контейнера (word wrap).
 *
 * Алгоритм:
 * 1. Разбиение строки на сегменты по UAX#14 soft break opportunities:
 *    - BA/HY/HH/SY: пробелы, дефис, слеш, en-dash (U+2013), figure-dash (U+2012), hyphen (U+2010) — break AFTER
 *    - B2: em-dash (U+2014) — break BEFORE и AFTER
 * 2. Жадная сборка: добавляем сегменты к текущей строке пока помещаются.
 * 3. Fallback: если один сегмент не влезает — посимвольный перенос (break-word).
 *
 * Чистая функция: принимает инжектируемый `measureWidth` — не зависит от opentype/canvas.
 *
 * @param rawLines — исходные строки (разделённые по `\n`)
 * @param measureWidth — функция измерения ширины
 * @param maxWidth — максимальная ширина строки. Если ≤ 0 — переносы отключены, вернётся `rawLines` как есть.
 */
export function wrapLines(
  rawLines: readonly string[],
  measureWidth: MeasureTextWidth,
  maxWidth: number,
  hyphenation?: HyphenationRules,
): string[] {
  return wrapLinesDetailed(rawLines, measureWidth, maxWidth, hyphenation).map(line => line.text);
}

/** {@link wrapLines} с признаком конца абзаца на каждой строке — см. {@link WrappedLine}. */
export function wrapLinesDetailed(
  rawLines: readonly string[],
  measureWidth: MeasureTextWidth,
  maxWidth: number,
  hyphenation?: HyphenationRules,
  languages?: WordLanguages,
): WrappedLine[] {
  if (maxWidth <= 0) return rawLines.map(text => ({ text, paragraphEnd: true }));

  const builder = new LineBuilder(text => measureWidth(text), hyphenation, undefined, languages);

  for (const rawLine of rawLines) {
    const segments = breakSegments(rawLine);
    let currentLine = '';

    for (const segment of segments) {
      builder.segEnd += segment.length;
      const testLine = currentLine + segment;
      const testWidth = measureBreakWidth(testLine, measureWidth);

      if (testWidth > maxWidth && currentLine.length > 0) {
        currentLine = builder.breakBefore(currentLine, segment, maxWidth);
      } else {
        currentLine = testLine;
      }

      currentLine = builder.breakOverflow(currentLine, () => maxWidth);
    }

    builder.push(currentLine, true);
    builder.segEnd += 1;
  }

  return builder.lines;
}

/**
 * The authored lines of a text before wrapping, for line-count estimates: paragraph breaks, and soft returns
 * ({@link SOFT_LINE_BREAK}) of a box from `textLayout` 7 — below it, or with the rule unknown, the renderer does not
 * break there and neither does the estimate.
 */
export function authoredLines(text: string, textLayout?: number): string[] {
  return text.split(textLayoutAtLeast(textLayout, TEXT_LAYOUT_PARAGRAPHS) ? PARAGRAPH_OR_SOFT_BREAK : '\n');
}

/** A line of source text closed by a forced break (`textLayout` 7) — mirror of Rust `ForcedLine`. */
export interface ForcedLine {
  readonly text: string;
  /** Index of the paragraph the line belongs to (counted by `\n`). */
  readonly paragraph: number;
  /** The line closes its paragraph; otherwise it ends at a soft return. */
  readonly paragraphEnd: boolean;
}

/** `text` cut at its forced breaks: `\n` closes a paragraph, {@link SOFT_LINE_BREAK} a line inside it. */
export function forcedLines(text: string): ForcedLine[] {
  const out: ForcedLine[] = [];
  text.split('\n').forEach((para, paragraph) => {
    const parts = para.split(SOFT_LINE_BREAK);
    parts.forEach((part, i) => out.push({ text: part, paragraph, paragraphEnd: i === parts.length - 1 }));
  });
  return out;
}

/**
 * Where {@link wrapParagraphLines} wraps: width of line `index` of `paragraph`, `first` on its first line. A frame that
 * wraps around page objects (`wrap-slots.ts`) makes each line one slot — one segment of a baseline — so two lines beside
 * an object share a baseline; `index` still counts lines. Asked in line order, only for lines that hold text.
 */
export interface ParagraphWidths {
  readonly widthAt: (index: number, paragraph: number, first: boolean) => number;
  /** Area-text contour: bands without width are skipped with empty lines. */
  readonly skipsDeadBands: boolean;
  /**
   * Told the break width of a line's first word and the source offset of the line before its width is first asked (the
   * wrap around page objects keeps a word out of a segment it does not fit). Absent — nobody asks.
   */
  readonly lineStart?: (word: number, start: number) => void;
}

/** Bands the dead-band skip may consume before giving up (mirror of area-shape `MAX_BANDS`). */
const MAX_DEAD_BANDS = 5000;

/**
 * Greedy wrap of `textLayout` 7 — mirror of Rust `wrap_paragraph_lines`: the same segments, hyphenation and
 * break-word fallback as {@link wrapLinesDetailed}, over {@link forcedLines}; each paragraph wraps to its own width, a
 * soft return ends a line without ending the paragraph, `measure` gets the candidate's source offset. `undefined`
 * widths — no frame (point text): each forced line stays one line.
 */
export function wrapParagraphLines(
  lines: readonly ForcedLine[],
  measure: MeasureTextAt,
  widths: ParagraphWidths | undefined,
  hyphenation?: HyphenationRules,
  languages?: WordLanguages,
): WrappedLine[] {
  if (!widths) {
    return lines.map(l =>
      l.paragraphEnd ? { text: l.text, paragraphEnd: true } : { text: l.text, paragraphEnd: false, lineForced: true },
    );
  }

  let paragraph = 0;
  let firstLine = 0;
  const widthNow = (): number => widths.widthAt(builder.lines.length, paragraph, builder.lines.length === firstLine);
  const skipDeadBands = (): void => {
    if (!widths.skipsDeadBands) return;
    while (widthNow() <= 0 && builder.lines.length < MAX_DEAD_BANDS) builder.push('', false);
  };

  const builder: LineBuilder = new LineBuilder(measure, hyphenation, skipDeadBands, languages);
  builder.lineStart = widths.lineStart;

  let opensParagraph = true;
  for (const line of lines) {
    skipDeadBands();
    if (opensParagraph) {
      paragraph = line.paragraph;
      firstLine = builder.lines.length;
    }

    let current = '';
    for (const segment of breakSegments(line.text)) {
      builder.segEnd += segment.length;
      if (current.length === 0) builder.startsLine(segment);
      const testLine = current + segment;
      const width = widthNow();
      current =
        builder.widthEndingHere(testLine) > width && current.length > 0 ? builder.breakBefore(current, segment, width) : testLine;
      current = builder.breakOverflow(current, widthNow);
    }

    if (line.paragraphEnd) builder.push(current, true);
    else builder.pushSoft(current);
    builder.segEnd += 1;
    opensParagraph = line.paragraphEnd;
  }

  return builder.lines;
}
